<template>
    <div
        class="filter-chip-container"
        :class="{ 'chip-container--removable': removable, 'chip-container--clearable': clearable }"
        :data-filter-field="dataFilterField"
    >
        <CustomPopover
            ref="customPopover"
            :icon="icon"
            :label="getLabel()"
            :show-dropdown-arrow="false"
            :button-class="chipClass + (removable ? ' chip--removable' : '') + (hasValue ? ' chip--has-value' : '')"
            :show-form-footer="true"
            :removable="removable"
            :clearable="isClearable"
            @popover-change="onPopoverChange"
            @clear="onPopoverClear"
            @apply="onPopoverApply"
            @remove-click="onRemove"
            @clear-click="onClear"
        >
            <template slot="content">
                <div class="broadcast-composite-container">
                    <div class="broadcast-composite-field">
                        <label class="broadcast-composite-label">{{ $t("auto.broadcast") }}</label>
                        <select v-model="broadcastValue" class="broadcast-composite-select">
                            <option value="">{{ $t("auto.select") }}</option>
                            <option v-for="option in broadcastOptions" :key="option.value" :value="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                    </div>
                    <div class="broadcast-composite-field">
                        <label class="broadcast-composite-label">{{ $t("auto.messageStatus") }}</label>
                        <select v-model="statusValue" class="broadcast-composite-select">
                            <option value="">{{ $t("auto.select") }}</option>
                            <option v-for="option in statusOptions" :key="option.value" :value="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                    </div>
                    <div class="broadcast-composite-field">
                        <label class="broadcast-composite-label">{{ $t("auto.replyStatus") }}</label>
                        <select v-model="answeredValue" class="broadcast-composite-select">
                            <option value="">{{ $t("auto.select") }}</option>
                            <option v-for="option in answeredOptions" :key="option.value" :value="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                    </div>
                </div>
            </template>
        </CustomPopover>
    </div>
</template>

<script>
import CustomPopover from "./CustomPopover.vue";
import WHATSAPP_API from "../../../api/whatsapp";

export default {
    name: "BroadcastCompositeFilterChip",
    components: {
        CustomPopover,
    },
    props: {
        value: {
            type: Object,
            default: () => ({}),
        },
        filterName: {
            type: String,
            default: "Toplu Mesaj",
        },
        icon: {
            type: String,
            default: "campaign",
        },
        removable: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        dataFilterField: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            broadcastValue: null,
            statusValue: null,
            answeredValue: null,
            whatsappBroadcasts: [],
        };
    },
    computed: {
        chipClass() {
            return "chip filter-chip";
        },
        hasValue() {
            return !!(this.broadcastValue || this.statusValue || this.answeredValue !== null);
        },
        isClearable() {
            return (
                this.clearable &&
                (this.broadcastValue || this.statusValue || this.answeredValue !== null)
            );
        },
        broadcastOptions() {
            return this.whatsappBroadcasts.map((broadcast) => ({
                label: broadcast.title,
                value: broadcast._id,
                icon: "campaign",
            }));
        },
        statusOptions() {
            return [
                { label: "Kuyrukta", value: "enqueued", icon: "queue" },
                { label: "Gönderildi", value: "sent", icon: "send" },
                { label: "Teslim Edildi", value: "delivered", icon: "done" },
                { label: "Okundu", value: "read", icon: "done_all" },
                { label: "Silindi", value: "delete", icon: "delete" },
                { label: "Başarısız", value: "failed", icon: "error" },
            ];
        },
        answeredOptions() {
            return [
                { label: "Yanıtlayanlar", value: true, icon: "reply" },
                { label: "Yanıtlamayanlar", value: false, icon: "reply_all" },
            ];
        },
    },
    async mounted() {
        await this.loadWhatsappBroadcasts();

        // Mevcut değerleri set et
        if (this.value) {
            this.broadcastValue = this.value.broadcast || null;
            this.statusValue = this.value.status || null;
            this.answeredValue = this.value.answered !== undefined ? this.value.answered : null;
        }
    },
    methods: {
        async loadWhatsappBroadcasts() {
            try {
                const broadcasts = await WHATSAPP_API.BROADCAST.getBroadcastList(50, "");
                this.whatsappBroadcasts = broadcasts || [];
            } catch (error) {
                this.whatsappBroadcasts = [];
            }
        },
        getLabel() {
            let labels = [];

            if (this.broadcastValue) {
                const broadcast = this.whatsappBroadcasts.find(b => b._id === this.broadcastValue);
                if (broadcast) labels.push(broadcast.title);
            }

            if (this.statusValue) {
                const status = this.statusOptions.find(s => s.value === this.statusValue);
                if (status) labels.push(status.label);
            }

            if (this.answeredValue !== null) {
                const answered = this.answeredOptions.find(a => a.value === this.answeredValue);
                if (answered) labels.push(answered.label);
            }

            if (labels.length > 0) {
                const valueText = labels.length > 1 ? `${labels[0]} (+${labels.length - 1})` : labels[0];
                return `${this.filterName}: ${valueText}`;
            }

            return this.filterName;
        },
        onPopoverChange() {
            // Popover açılıp kapandığında yapılacak işlemler
        },
        onPopoverClear() {
            this.broadcastValue = null;
            this.statusValue = null;
            this.answeredValue = null;
            this.$emit("change", null);
        },
        onPopoverApply() {
            const value = {};

            if (this.broadcastValue) {
                value.broadcast = this.broadcastValue;
            }

            if (this.statusValue) {
                value.status = this.statusValue;
            }

            if (this.answeredValue !== null) {
                value.answered = this.answeredValue;
            }

            // En az bir değer seçilmişse emit et
            if (Object.keys(value).length > 0) {
                this.$emit("change", value);
            } else {
                this.$emit("change", null);
            }
        },
        onRemove() {
            this.$emit("remove");
        },
        onClear() {
            this.onPopoverClear();
        },
        openPopover() {
            if (this.$refs.customPopover) {
                this.$refs.customPopover.openPopover();
            }
        },
        closePopover() {
            if (this.$refs.customPopover) {
                this.$refs.customPopover.closePopover();
            }
        },
    },
};
</script>

<style lang="scss">
.broadcast-composite-container {
    padding: 16px;
    min-width: 320px;
}

.broadcast-composite-field {
    margin-bottom: 16px;

    &:last-child {
        margin-bottom: 0;
    }
}

.broadcast-composite-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
}

.broadcast-composite-select {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 13px;
    background: white;
    color: #374151;

    &:focus {
        outline: none;
        border-color: #22272d;
        box-shadow: 0 0 0 2px rgba(34, 39, 45, 0.1);
    }

    option {
        padding: 4px 8px;
    }
}
</style>
